import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Funnel, Download } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const money = (n) => (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function csvDownload(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => { if (v == null) return ""; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob); link.download = filename; link.click();
  URL.revokeObjectURL(link.href);
}

export default function PicksHistory() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [analystFilter, setAnalystFilter] = useState("ALL");
  const [outcomeFilter, setOutcomeFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState("ALL"); // ALL | 7 | 14 | 30

  const isAdmin = !!(user && user.role === "admin");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/picks/feed");
      setData(data);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let cutoff = null;
    if (dateRange !== "ALL") {
      const d = new Date();
      d.setDate(d.getDate() - Number(dateRange));
      cutoff = d.toISOString().slice(0, 10);
    }
    return data.picks
      .filter((p) => analystFilter === "ALL" || p.analyst === analystFilter)
      .filter((p) => outcomeFilter === "ALL" || p.result === outcomeFilter)
      .filter((p) => !cutoff || (p.date && p.date >= cutoff))
      .reverse();
  }, [data, analystFilter, outcomeFilter, dateRange]);

  const stats = useMemo(() => {
    if (!filtered.length) return { bets: 0, wins: 0, losses: 0, profit: 0, wr: 0 };
    const placed = filtered.filter((p) => p.result === "W" || p.result === "L");
    const wins = placed.filter((p) => p.result === "W").length;
    return {
      bets: placed.length, wins, losses: placed.length - wins,
      profit: placed.reduce((s, p) => s + (p.profit || 0), 0),
      wr: placed.length ? wins / placed.length : 0,
    };
  }, [filtered]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Track Record</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">PREDICTION HISTORY</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border border-white/15 rounded-sm overflow-hidden" data-testid="history-date-range">
            {[
              { k: "7", label: "7d" },
              { k: "14", label: "14d" },
              { k: "30", label: "30d" },
              { k: "ALL", label: "All" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setDateRange(opt.k)}
                data-testid={`history-range-${opt.k.toLowerCase()}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border-l border-white/15 first:border-l-0 transition-colors ${
                  dateRange === opt.k ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:bg-white/5"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Funnel size={16} className="text-zinc-500" />
          <select data-testid="history-analyst-filter" value={analystFilter} onChange={(e) => setAnalystFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d">
            <option value="ALL">All Analysts</option>
            <option value="Charlie">Charlie</option>
            <option value="Dakota">Dakota</option>
          </select>
          <select data-testid="history-outcome-filter" value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-1.5 text-sm font-mono-d">
            <option value="ALL">All Outcomes</option>
            <option value="W">Wins only</option>
            <option value="L">Losses only</option>
            <option value="SKIP-WEEK">Skipped (weekly)</option>
            <option value="SKIP-BOTH">Skipped (both teams)</option>
          </select>
          <button onClick={() => csvDownload("picks_history.csv", filtered)} data-testid="history-csv"
            className="flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider rounded-sm">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-zinc-500 font-mono-d" data-testid="history-loading">Loading history…</div>
      ) : (
        <>
          <div className={`grid grid-cols-2 ${isAdmin ? "md:grid-cols-5" : "md:grid-cols-3"} gap-3 mb-6`}>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Picks</div>
              <div className="font-display text-3xl">{stats.bets}</div>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Record</div>
              <div className="font-display text-3xl">{stats.wins}-{stats.losses}</div>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Hit Rate</div>
              <div className={`font-display text-3xl ${stats.wr >= 0.63 ? "text-[#22C55E]" : "text-zinc-300"}`}>
                {(stats.wr * 100).toFixed(1)}%
              </div>
            </div>
            {isAdmin && (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4 md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Net Result</div>
                <div className={`font-display text-3xl ${stats.profit > 0 ? "text-[#22C55E]" : stats.profit < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                  {money(stats.profit)}
                </div>
              </div>
            )}
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[640px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-black/90 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-2 py-2">Analyst</th>
                    <th className="text-left px-2 py-2">Pick</th>
                    <th className="text-left px-2 py-2">Opponent</th>
                    {isAdmin && <th className="text-right px-2 py-2">Stake</th>}
                    <th className="text-center px-2 py-2">Result</th>
                    {isAdmin && <th className="text-right px-3 py-2">P/L</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => (
                    <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="border-b border-white/5 hover:bg-white/5" data-testid="history-row">
                      <td className="px-3 py-1.5 font-mono-d text-zinc-300 text-xs">{p.date}</td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                          p.analyst === "Charlie" ? "bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40"
                          : "bg-yellow-400/20 text-yellow-400 border border-yellow-400/40"
                        }`}>{p.analyst}</span>
                      </td>
                      <td className="px-2 py-1.5 font-head font-bold">{p.team}</td>
                      <td className="px-2 py-1.5 text-zinc-400 text-xs">{p.opponent}</td>
                      {isAdmin && (
                        <td className="px-2 py-1.5 text-right font-mono-d text-xs text-zinc-300">
                          {p.stake ? `$${p.stake.toFixed(2)}` : "—"}
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-center">
                        {p.result === "W" ? <span className="bg-[#22C55E] text-black font-bold text-xs px-2 py-0.5 rounded-sm">W</span>
                         : p.result === "L" ? <span className="bg-[#FF3B30] text-black font-bold text-xs px-2 py-0.5 rounded-sm">L</span>
                         : <span className="text-[10px] uppercase text-yellow-400 px-1.5 py-0.5 border border-yellow-400/40 rounded-sm">{p.result}</span>}
                      </td>
                      {isAdmin && (
                        <td className={`px-3 py-1.5 text-right font-mono-d text-xs ${
                          p.profit > 0 ? "text-[#22C55E]" : p.profit < 0 ? "text-[#FF3B30]" : "text-zinc-500"
                        }`}>{p.profit ? money(p.profit) : "—"}</td>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
